import { useEffect } from 'react';
import { Form, Formik } from 'formik';
import Button from 'src/components/button/Button';
import ProfilePicture from 'src/components/profile/profilePicture/ProfilePicture';
import InputField from 'src/components/common/formFields/inputField/InputField';
import { componentSizeVariantsEnum } from 'src/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { TEditCommentRequest } from 'src/services/apiEndpoint.types.ts';
import { TUserTypes } from 'src/constants/user.constants';

import './editComment.scss';
import editCommentValidationSchema from './editComment.validationSchema';
import { editComment } from 'src/store/venture/venture.slice';
import { useGetIdentity } from 'src/hooks';

export type TEditComment = {
  onClose: () => void;
  comment: any;
  ventureVerseId: string;
  handleGetComments: () => void;
  viewerId: string;
  viewerType: TUserTypes;
};

export type TEditCommentFormValues = {
  content: string;
};

// This component must be aware of the id and type of the viewer
const EditComment = ({
  comment,
  ventureVerseId,
  onClose,
  handleGetComments,
  viewerId,
  viewerType
}: TEditComment) => {
  const initialValues: TEditCommentFormValues = { content: comment?.content };

  const dispatch = useAppDispatch();
  const { getIdentities } = useGetIdentity();

  const editCommentLoader: boolean = useAppSelector((store) => store.venture.editCommentLoader);

  const commentId = comment?.id;
  const commentEditorProfilePic =
    viewerType === 'User' ? comment?.User?.profilePic : comment?.Company?.logo;
  const commentEditorName = comment?.User?.name || comment?.Company?.name;
  const { authenticatedId, loggedInUserType } = getIdentities();

  const handleSubmit = async (
    { content }: TEditCommentFormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    // edit comment api call
    const payload: TEditCommentRequest = {
      ...(loggedInUserType === 'Company' ? { companyId: viewerId } : { userId: viewerId }),
      id: commentId,
      ventureVerseId,
      content
    };

    dispatch(editComment(payload)).then(() => {
      handleGetComments();
      resetForm();
      onClose();
    });
  };

  useEffect(() => {
    initialValues.content = comment?.content;
  }, [JSON.stringify(comment)]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={editCommentValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, values }) => {
          return (
            <Form noValidate className="create-comment">
              <div className="create-comment__header">
                <ProfilePicture
                  url={commentEditorProfilePic || ''}
                  fallback={commentEditorName?.[0] || ''}
                />
                <InputField
                  placeholder="Comment"
                  id="text"
                  variant={componentSizeVariantsEnum.SMALL}
                  onChange={(e) => {
                    setFieldValue('content', e.target.value);
                  }}
                  value={values.content}
                  errorMessage={errors.content}
                  isTouched={touched.content}
                />
              </div>
              <div className="create-comment__buttons">
                <Button variant="secondary" size="small" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary" size="small" type="submit" loading={editCommentLoader}>
                  Save
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default EditComment;
